/**
 * Provider Onboarding - Corrected Flow
 * P0 FIX: Payment LOCKED until TreatX approval
 * 
 * Flow Order:
 * 1. Provider Type Selection (Hospital / Clinic / Medical Doctor)
 * 2. Legal & Identity Information
 * 3. Geolocation Capture
 * 4. Document Upload (Mandatory)
 * 5. Submission Complete → Status: "Pending TreatX Validation"
 * 
 * AFTER TreatX Approval ONLY:
 * 6. Package Selection
 * 7. Payment
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
import { 
  useProviderOnboarding, 
  PROVIDER_TYPES, 
  SUBSCRIPTION_PACKAGES, 
  ONBOARDING_STATUS 
} from '../../context/ProviderOnboardingContext';
// TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN
import {
  startRegistrationSession,
  completeSession,
  loadStoredSession,
  persistSession,
  clearStoredSession,
  loadOnboardingProgress,
  persistOnboardingProgress,
  clearOnboardingProgress,
} from '../../utils/onboardingSession';
import WaitingForEmailVerificationStep from '../onboarding/WaitingForEmailVerificationStep';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SafeHtml } from '../../components/security/SafeHtml';
import { Badge } from '../../components/ui/badge';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../components/ui/phone-input';
import {
  Building2, ChevronRight, ChevronLeft, Check,
  MapPin, FileText, Shield, CheckCircle,
  Upload, Download, AlertCircle, Globe, Phone, Mail,
  Building, FileCheck, Clock, X, Info, Loader2, Lock, ExternalLink
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DateField from '../../components/DateField';
import { api } from '../../utils/api';

// TREATX-AGREEMENT-MANAGEMENT-MVP-01 — legal agreement list is now
// DB-driven. The wizard fetches the currently-published set for the
// `provider` audience from `GET /api/agreements/current` on mount and
// derives every downstream check (submit gate, submit payload,
// disabled-button predicate) from that dynamic list. Nothing else in
// this file hard-codes agreement keys anymore.
//
// Response contract per key:
//   { content_key, version, title, summary, body_html, body_sha256,
//     locale, is_locale_fallback, effective_from, ... }


// Step Progress Indicator
const StepIndicator = ({ currentStep, steps }) => (
  <div className="w-full bg-white border-b">
    <div className="max-w-5xl mx-auto px-4 py-4">
      <div className="flex items-center justify-between overflow-x-auto">
        {steps.map((step, idx) => (
          <React.Fragment key={step.id}>
            <div className={`flex items-center gap-2 flex-shrink-0 ${
              step.locked ? 'text-gray-300' :
              currentStep >= step.id ? 'text-blue-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step.locked ? 'bg-gray-100 text-gray-400' :
                currentStep > step.id ? 'bg-green-500 text-white' :
                currentStep === step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step.locked ? <Lock className="h-3 w-3" /> : 
                 currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
              </div>
              <span className="text-xs font-medium hidden md:inline whitespace-nowrap">{step.title}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 min-w-[20px] ${
                step.locked ? 'bg-gray-100' :
                currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

// Step 1: Provider Type Selection
const ProviderTypeStep = ({ selected, onSelect }) => {
  const { t } = useTranslation();
  // Locale-agnostic override of PROVIDER_TYPES.name/description without
  // mutating the backend/context IDs. If a key is not provisioned we fall
  // back to the context-provided value.
  const typeLabel = (id, field) => {
    const key = `provider.providerTypes.${id}_${field}`;
    const s = t(key);
    if (s && s !== key) return s;
    const fromCtx = PROVIDER_TYPES[id];
    return fromCtx ? fromCtx[field] : '';
  };
  return (
  <div className="space-y-4">
    <div className="text-center mb-4">
      <h2 className="text-2xl font-bold text-gray-900">{t('provider.onboarding.typeTitle')}</h2>
      <p className="text-gray-600 mt-1 text-sm">{t('provider.onboarding.typeSubtitle')}</p>
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Object.values(PROVIDER_TYPES).map(type => (
        <Card
          key={type.id}
          className={`cursor-pointer transition-all hover:shadow-md ${
            selected === type.id ? 'ring-2 ring-blue-600 shadow-md' : ''
          }`}
          onClick={() => onSelect(type.id)}
          data-testid={`provider-type-${type.id}`}
        >
          <CardContent className="pt-4 pb-4 text-center">
            <div className="text-3xl mb-2">{type.icon}</div>
            <h3 className="text-base font-bold mb-1">{typeLabel(type.id, 'name')}</h3>
            <p className="text-gray-500 text-xs leading-snug">{typeLabel(type.id, 'desc')}</p>
            {selected === type.id && (
              <div className="mt-2">
                <Badge className="bg-blue-600 text-xs">{t('provider.onboarding.selectedBadge')}</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
      <div className="flex gap-2">
        <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-blue-800 font-medium">{t('provider.onboarding.legalEntityBannerTitle')}</p>
          <p className="text-blue-700 mt-0.5 leading-snug"
             dangerouslySetInnerHTML={{ __html: t('provider.onboarding.legalEntityBannerBody') }} />
        </div>
      </div>
    </div>
  </div>
  );
};

// Step 2: Legal Information
const LegalInfoStep = ({ providerType, formData, onChange }) => {
  const { t } = useTranslation();
  const isIndividual = providerType === 'doctor_practice';
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('provider.onboarding.legalTitle')}</h2>
        <p className="text-gray-600 mt-1 text-sm">
          {isIndividual ? t('provider.onboarding.legalSubtitleIndividual') : t('provider.onboarding.legalSubtitleOrg')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-blue-600" />
              {isIndividual ? t('provider.onboarding.personalInformation') : t('provider.onboarding.organizationInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
          {isIndividual ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('provider.onboarding.fullNameLabel')}</Label>
                  <Input
                    value={formData.fullName || ''}
                    onChange={(e) => onChange('fullName', e.target.value)}
                    placeholder={t('provider.onboarding.fullNamePlaceholder')}
                    data-testid="legal-fullname"
                  />
                </div>
                <div>
                  <Label>{t('provider.onboarding.medicalTitleLabel')}</Label>
                  <Input
                    value={formData.medicalTitle || ''}
                    onChange={(e) => onChange('medicalTitle', e.target.value)}
                    placeholder={t('provider.onboarding.medicalTitlePlaceholder')}
                    data-testid="legal-medical-title"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('provider.onboarding.nationalIdLabel')}</Label>
                  <Input
                    value={formData.nationalId || ''}
                    onChange={(e) => onChange('nationalId', e.target.value)}
                    placeholder={t('provider.onboarding.nationalIdPlaceholder')}
                    data-testid="legal-national-id"
                  />
                </div>
                <div>
                  <Label>{t('provider.onboarding.licenseNumberLabel')}</Label>
                  <Input
                    value={formData.licenseNumber || ''}
                    onChange={(e) => onChange('licenseNumber', e.target.value)}
                    placeholder={t('provider.onboarding.licenseNumberPlaceholder')}
                    data-testid="legal-license"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>{t('provider.onboarding.legalEntityNameLabel')} <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.legalName || ''}
                  onChange={(e) => onChange('legalName', e.target.value)}
                  placeholder={t('provider.onboarding.legalEntityNamePlaceholder')}
                  data-testid="legal-entity-name"
                />
                <p className="text-[11px] text-slate-500 mt-1" data-testid="legal-entity-name-helper">
                  {t('provider.onboarding.legalEntityNameHelper')}
                </p>
              </div>
              {/* TREATX-L1-D / Batch 2 — explicit patient-facing brand input.
                  REQUIRED and explicitly declared: the Provider must supply the
                  name patients see in Search / Marketplace. No silent fallback —
                  if there is no separate brand, the Provider enters the legal
                  entity name here as well (an explicit declaration). */}
              <div>
                <Label>{t('provider.onboarding.brandNameLabel')} <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.brandName || ''}
                  onChange={(e) => onChange('brandName', e.target.value)}
                  placeholder={t('provider.onboarding.brandNamePlaceholder')}
                  data-testid="legal-brand-name"
                />
                <p className="text-[11px] text-slate-500 mt-1" data-testid="legal-brand-name-helper">
                  {t('provider.onboarding.brandNameHelper')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('provider.onboarding.tradeRegistryLabel')}</Label>
                  <Input
                    value={formData.tradeRegistryNumber || ''}
                    onChange={(e) => onChange('tradeRegistryNumber', e.target.value)}
                    placeholder={t('provider.onboarding.tradeRegistryPlaceholder')}
                    data-testid="legal-trade-registry"
                  />
                </div>
                <div>
                  <Label>{t('provider.onboarding.entityTypeLabel')}</Label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={formData.entityType || ''}
                    onChange={(e) => onChange('entityType', e.target.value)}
                    data-testid="legal-entity-type"
                  >
                    <option value="">{t('provider.onboarding.entityTypeSelect')}</option>
                    <option value="llc">{t('provider.onboarding.entityLLC')}</option>
                    <option value="corporation">{t('provider.onboarding.entityCorporation')}</option>
                    <option value="partnership">{t('provider.onboarding.entityPartnership')}</option>
                    <option value="sole_proprietorship">{t('provider.onboarding.entitySoleProp')}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('provider.onboarding.taxOfficeLabel')}</Label>
                  <Input
                    value={formData.taxOffice || ''}
                    onChange={(e) => onChange('taxOffice', e.target.value)}
                    placeholder={t('provider.onboarding.taxOfficePlaceholder')}
                    data-testid="legal-tax-office"
                  />
                </div>
                <div>
                  <Label>{t('provider.onboarding.taxNumberLabel')}</Label>
                  <Input
                    value={formData.taxNumber || ''}
                    onChange={(e) => onChange('taxNumber', e.target.value)}
                    placeholder={t('provider.onboarding.taxNumberPlaceholder')}
                    data-testid="legal-tax-number"
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-4 w-4 text-blue-600" /> {t('provider.onboarding.contactInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t('provider.onboarding.officialEmailLabel')}</Label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                placeholder={t('provider.onboarding.officialEmailPlaceholder')}
                data-testid="legal-email"
              />
            </div>
            <div>
              <Label>{t('provider.onboarding.phoneLabel')}</Label>
              <PhoneInput
                value={formData.phone || ''}
                onChange={(e164) => onChange('phone', e164)}
                testId="legal-phone"
                placeholder={t('provider.onboarding.phonePlaceholder')}
                required
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account credentials */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4 text-blue-600" /> {t('provider.onboarding.accountCredentials')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            {t('provider.onboarding.credentialsHint')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('provider.onboarding.passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="provider-registration-new-password"
                  name="new-password"
                  type={showPassword ? 'text' : 'password'}
                  // PASSWORD-FIELD-SEMANTICS-01 — this is a NEW credential,
                  // not a sign-in field. Without `autoComplete="new-password"`
                  // (and a distinct name/id) the browser classified it as a
                  // login password field and autofilled the previously saved
                  // password into a provider REGISTRATION form. `new-password`
                  // is the standard signal that keeps password managers fully
                  // supported (they offer to GENERATE and then save) while not
                  // injecting an existing stored credential.
                  autoComplete="new-password"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  value={formData.password || ''}
                  onChange={(e) => onChange('password', e.target.value)}
                  placeholder={t('provider.onboarding.passwordPlaceholder')}
                  data-testid="legal-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <X className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                </button>
              </div>
              {formData.password && formData.password.length < 8 && (
                <p className="text-xs text-red-500 mt-1">{t('provider.onboarding.passwordTooShort')}</p>
              )}
            </div>
            <div>
              <Label>{t('provider.onboarding.confirmPasswordLabel')}</Label>
              <Input
                id="provider-registration-confirm-password"
                name="confirm-new-password"
                type={showPassword ? 'text' : 'password'}
                // PASSWORD-FIELD-SEMANTICS-01 — see the password field above.
                autoComplete="new-password"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                value={formData.confirmPassword || ''}
                onChange={(e) => onChange('confirmPassword', e.target.value)}
                placeholder={t('provider.onboarding.confirmPasswordPlaceholder')}
                data-testid="legal-confirm-password"
              />
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">{t('provider.onboarding.passwordsMismatch')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Step 3: Geolocation
// PROVIDER-ONBOARDING-CLEANUP-01 (2026-02): the previous Geolocation step
// rendered a fake "map preview" with hardcoded Istanbul defaults and a
// 5-city keyword resolver. Both removed. We now collect country + city +
// address as plain inputs; coordinates are no longer invented.
const AddressStep = ({ formData, onChange }) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API_URL}/api/public/catalog/countries-full`)
      .then(r => r.json())
      .then(d => { if (d.success && Array.isArray(d.countries)) setCountries(d.countries); })
      .catch(() => {});
  }, []);
  return (
  <div className="space-y-4">
    <div className="text-center mb-4">
      <h2 className="text-2xl font-bold text-gray-900">{t('provider.onboarding.addressTitle')}</h2>
      <p className="text-gray-600 mt-1 text-sm">{t('provider.onboarding.addressSubtitle')}</p>
    </div>

    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-blue-600" />
          {t('provider.onboarding.addressDetailsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t('provider.onboarding.countryLabel')}</Label>
            <select
              className="w-full border rounded-lg p-2 text-sm"
              value={formData.country || ''}
              onChange={(e) => onChange('country', e.target.value)}
              data-testid="provider-onboarding-country"
            >
              <option value="">{t('provider.onboarding.countrySelectPlaceholder')}</option>
              {countries.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t('provider.onboarding.cityLabel')}</Label>
            <Input
              value={formData.city || ''}
              onChange={(e) => onChange('city', e.target.value)}
              placeholder={t('provider.onboarding.cityPlaceholder')}
              data-testid="provider-onboarding-city"
            />
          </div>
        </div>
        <div>
          <Label>{t('provider.onboarding.fullAddressLabel')}</Label>
          <Textarea
            value={formData.address || ''}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder={t('provider.onboarding.fullAddressPlaceholder')}
            rows={2}
            data-testid="provider-onboarding-address"
          />
        </div>
        <div>
          <Label>{t('provider.onboarding.districtLabel')}</Label>
          <Input
            value={formData.district || ''}
            onChange={(e) => onChange('district', e.target.value)}
            placeholder={t('provider.onboarding.districtPlaceholder')}
          />
        </div>
      </CardContent>
    </Card>
  </div>
  );
};

// DOC-VALIDATION-01 — structural validator for one canonical requirement
// row. Mirrors the backend contract WITHOUT hardcoding document types or
// counts: identity must be a non-empty string, mandatory must be an explicit
// boolean, and min_count (when present) must be a sane positive number.
const isValidRequirementRow = (r) => (
  !!r
  && typeof r === 'object'
  && !Array.isArray(r)
  && typeof r.document_type_id === 'string'
  && r.document_type_id.trim().length > 0
  && typeof r.mandatory === 'boolean'
  && (r.min_count === undefined || r.min_count === null
    || (typeof r.min_count === 'number' && Number.isFinite(r.min_count) && r.min_count >= 1))
);

// Step 4: Document Upload — PRD1.9 Blocker 5: Uses central document engine
const DocumentUploadStep = ({ applicationId, documents, onUpload, onUploadComplete }) => {
  const { t } = useTranslation();
  const [uploadedDocs, setUploadedDocs] = useState(documents || {});
  const [uploading, setUploading] = useState({});
  const [expiryDates, setExpiryDates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [requirements, setRequirements] = useState([]);
  // DOC-VALIDATION-01 — an empty `requirements` array cannot distinguish
  // "still loading", "the canonical read failed", "the response was
  // malformed" and "no requirements are configured". The step-4 Next gate
  // used to fail OPEN for all four, so this explicit state is reported
  // upward and the gate fails CLOSED unless it is 'ready'.
  const [reqState, setReqState] = useState('loading'); // loading|ready|empty|error
  const [reqReloadKey, setReqReloadKey] = useState(0);
  const loadingReqs = reqState === 'loading';

  // Fetch requirements from backend if application exists
  useEffect(() => {
    if (!applicationId) {
      // No application ⇒ the canonical requirement set cannot be read at all.
      setRequirements([]);
      setReqState('error');
      return undefined;
    }
    let cancelled = false;
    const fetchReqs = async () => {
      setReqState('loading');
      try {
        // Application-private: uses the canonical credentialed api client so
        // the application-bound status_session cookie (minted at /complete or
        // return-portal exchange) authorizes the read.
        const res = await api.get(`/provider/applications/${applicationId}/requirements`);
        const data = res.data;
        if (cancelled) return;

        // Envelope + per-row structural validation against the canonical
        // contract (`routes/provider_domain_routes.py:_get_requirements_with_docs`).
        // Anything unsuccessful, non-array or structurally ambiguous is an
        // ERROR — never 'ready'.
        const rows = data && data.success === true && Array.isArray(data.requirements)
          ? data.requirements
          : null;
        if (!rows || !rows.every(isValidRequirementRow)) {
          setRequirements([]);
          setReqState('error');
          return;
        }
        if (rows.length === 0) {
          setRequirements([]);
          setReqState('empty');
          return;
        }

        setRequirements(rows);
        setReqState('ready');
        // Pre-populate uploaded docs from existing documents
        for (const req of rows) {
          if (req.documents?.length) {
            const active = req.documents.find(d => !d.superseded);
            if (active) {
              setUploadedDocs(prev => ({
                ...prev,
                [req.document_type_id]: {
                  name: active.file_name,
                  size: active.file_size,
                  type: active.mime_type,
                  document_id: active.document_id,
                  uploaded: true,
                }
              }));
            }
          }
        }
      } catch (e) {
        if (cancelled) return;
        console.warn('[DocEngine] Could not fetch requirements:', e?.message || e);
        setRequirements([]);
        setReqState('error');
      }
    };
    fetchReqs();
    return () => { cancelled = true; };
  }, [applicationId, reqReloadKey]);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFileSelect = async (docId, file) => {
    setUploadErrors(prev => ({ ...prev, [docId]: null }));

    if (!applicationId) {
      setUploadErrors(prev => ({ ...prev, [docId]: t('provider.onboarding.docs.errAppNotReady') }));
      return;
    }

    setUploading(prev => ({ ...prev, [docId]: true }));

    try {
      const base64Data = await fileToBase64(file);
      // Application-private mutation: canonical credentialed api client
      // attaches the status_session cookie + X-CSRF-Token automatically.
      const res = await api.post(`/provider/applications/${applicationId}/documents/upload`, {
        document_type_id: docId,
        filename: file.name,
        file_data: base64Data,
        expiry_date: expiryDates[docId] || null,
      });
      const data = res.data;

      if (data.success) {
        const fileData = {
          name: file.name,
          size: file.size,
          type: file.type,
          document_id: data.document.document_id,
          uploaded: true,
        };
        setUploadedDocs(prev => ({ ...prev, [docId]: fileData }));
        setUploadErrors(prev => ({ ...prev, [docId]: null }));
        onUpload(docId, fileData);
        toast({ title: t('provider.onboarding.docs.toastUploadedTitle'), description: t('provider.onboarding.docs.toastUploadedDesc', { name: file.name }) });
      } else {
        const errMsg = data.detail || data.message || t('provider.onboarding.docs.errUploadFailed');
        setUploadErrors(prev => ({ ...prev, [docId]: errMsg }));
      }
    } catch (e) {
      console.error('[DocEngine] Upload error:', e);
      const d = e?.response?.data;
      const errMsg = (d && (typeof d.detail === 'string' ? d.detail : d.message))
        || t('provider.onboarding.docs.errNetwork');
      setUploadErrors(prev => ({ ...prev, [docId]: errMsg }));
    }

    setUploading(prev => ({ ...prev, [docId]: false }));
  };

  const handleTemplateDownload = (doc) => {
    if (doc.cmsKey) {
      toast({ title: t('provider.onboarding.docs.toastTemplateTitle'), description: t('provider.onboarding.docs.toastTemplateDesc', { name: doc.name }), variant: 'destructive' });
    }
  };

  // Build doc list: ONLY from backend requirements — NO hardcoded fallback
  const allDocTypes = requirements.length > 0
    ? requirements.map(req => ({
        id: req.document_type_id,
        name: req.document_type_name || req.document_type_id,
        description: '',
        required: req.mandatory,
        requirementSnapshotId: req.rule_id,
        expiryRequired: req.expiry_required,
        category: req.category || 'general',
        uploadedCount: req.doc_count || 0,
        requiredCount: req.min_count || 1,
      }))
    : [];

  const noRequirementsConfigured = reqState === 'empty';
  const requirementsUnavailable = reqState === 'error';

  const treatxDocs = allDocTypes.filter(d => d.category === 'verification' || d.category === 'legal');
  const providerDocs = allDocTypes.filter(d => !['verification', 'legal'].includes(d.category));
  // If categories don't split evenly, show all in one group
  const group1 = treatxDocs.length > 0 ? treatxDocs : allDocTypes;
  const group2 = treatxDocs.length > 0 && providerDocs.length > 0 ? providerDocs : [];

  const getMandatoryCount = () => {
    const required = allDocTypes.filter(d => d.required);
    const uploaded = required.filter(d => uploadedDocs[d.id]?.uploaded);
    return { uploaded: uploaded.length, total: required.length };
  };

  const counts = getMandatoryCount();

  // BUG-4 / DOC-VALIDATION-01 — report the canonical requirement-load state
  // AND mandatory completeness to the wizard. `state` is the authority for
  // the step-4 gate; `known` is kept as a derived alias.
  useEffect(() => {
    onUploadComplete?.({
      state: reqState,
      known: reqState === 'ready',
      uploaded: counts.uploaded,
      total: counts.total,
    });
     
  }, [reqState, counts.uploaded, counts.total]);

  const renderDocRow = (doc) => {
    const isUploading = uploading[doc.id];
    const isUploaded = uploadedDocs[doc.id]?.uploaded;
    const needsExpiry = doc.expiryRequired && !expiryDates[doc.id];
    const docError = uploadErrors[doc.id];

    return (
      <div key={doc.id} className={`p-4 rounded-lg space-y-2 ${docError ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`} data-testid={`doc-row-${doc.id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-400" />
            <div>
              <p className="font-medium text-sm">
                {doc.name}
                {doc.required && <span className="text-red-500 ml-1">*</span>}
              </p>
              {doc.description && <p className="text-xs text-gray-500">{doc.description}</p>}
              <div className="flex items-center gap-1.5 mt-1">
                {doc.required ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium" data-testid={`mandatory-badge-${doc.id}`}>{t('provider.onboarding.docs.badgeMandatory')}</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">{t('provider.onboarding.docs.badgeOptional')}</span>
                )}
                {doc.expiryRequired && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" data-testid={`expiry-badge-${doc.id}`}>{t('provider.onboarding.docs.badgeExpiryRequired')}</span>
                )}
                {doc.status && doc.status !== 'pending' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${doc.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {doc.status}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isUploading ? (
              <Badge className="bg-blue-100 text-blue-700">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> {t('provider.onboarding.docs.uploading')}
              </Badge>
            ) : isUploaded ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700" data-testid={`uploaded-badge-${doc.id}`}>
                  <Check className="h-3 w-3 mr-1" /> {uploadedDocs[doc.id]?.name || t('provider.onboarding.docs.uploaded')}
                </Badge>
                <label className={needsExpiry ? 'pointer-events-none opacity-50' : 'cursor-pointer'}>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={needsExpiry}
                    onChange={(e) => e.target.files[0] && handleFileSelect(doc.id, e.target.files[0])}
                  />
                  <Button variant="ghost" size="sm" className="text-xs text-blue-600" asChild disabled={needsExpiry}>
                    <span>{t('provider.onboarding.docs.reupload')}</span>
                  </Button>
                </label>
              </div>
            ) : (
              <label className={needsExpiry ? 'pointer-events-none' : 'cursor-pointer'}>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={needsExpiry}
                  onChange={(e) => e.target.files[0] && handleFileSelect(doc.id, e.target.files[0])}
                  data-testid={`upload-input-${doc.id}`}
                />
                <Button variant="outline" size="sm" asChild disabled={needsExpiry} data-testid={`upload-btn-${doc.id}`}>
                  <span className={needsExpiry ? 'opacity-50 cursor-not-allowed' : ''}>
                    <Upload className="h-4 w-4 mr-1" /> {t('provider.onboarding.docs.upload')}
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>
        {/* Expiry date field — only when requirement demands it */}
        {doc.expiryRequired && (
          <div className="ml-8 space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-amber-700 font-medium">{t('provider.onboarding.docs.expiryDateLabel')}</Label>
              <DateField
                value={expiryDates[doc.id] || ''}
                onChange={(iso) => {
                  setExpiryDates(prev => ({ ...prev, [doc.id]: iso }));
                  setUploadErrors(prev => ({ ...prev, [doc.id]: null }));
                }}
                className={`h-7 text-xs w-40 ${needsExpiry ? 'border-amber-400' : 'border-green-400'}`}
                data-testid={`expiry-${doc.id}`}
              />
              {!needsExpiry && <Check className="h-3.5 w-3.5 text-green-500" />}
            </div>
            {needsExpiry && !isUploaded && (
              <p className="text-[11px] text-amber-600 flex items-center gap-1" data-testid={`expiry-hint-${doc.id}`}>
                <AlertCircle className="h-3 w-3" /> {t('provider.onboarding.docs.expiryHint')}
              </p>
            )}
          </div>
        )}
        {/* Inline error message */}
        {docError && (
          <p className="ml-8 text-xs text-red-600 flex items-center gap-1" data-testid={`upload-error-${doc.id}`}>
            <AlertCircle className="h-3 w-3 flex-shrink-0" /> {docError}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">{t('provider.onboarding.docs.title')}</h2>
        <p className="text-gray-600 mt-2">{t('provider.onboarding.docs.subtitle')}</p>
        {applicationId && (
          <p className="text-xs text-gray-400 mt-1">{t('provider.onboarding.docs.applicationLabel', { id: applicationId })}</p>
        )}
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">{t('provider.onboarding.docs.noticeTitle')}</p>
            <p className="text-sm text-amber-700 mt-1">
              {t('provider.onboarding.docs.noticeBodyPart1')}{' '}
              {t('provider.onboarding.docs.noticeBodyPart2')} <strong>{t('provider.onboarding.docs.noticeBodyStrong')}</strong>{t('provider.onboarding.docs.noticeBodyPart3')}
            </p>
          </div>
        </div>
      </div>

      {loadingReqs ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          {t('provider.onboarding.docs.loadingReqs')}
        </div>
      ) : requirementsUnavailable ? (
        <Card className="border-red-200 bg-red-50" data-testid="requirements-unavailable-block">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">{t('provider.onboarding.docs.reqErrorTitle')}</h3>
            <p className="text-sm text-red-600 max-w-md mx-auto">
              {t('provider.onboarding.docs.reqErrorBody')}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={() => setReqReloadKey(k => k + 1)}
              data-testid="requirements-retry-button"
            >
              {t('provider.onboarding.docs.reqRetry')}
            </Button>
          </CardContent>
        </Card>
      ) : noRequirementsConfigured ? (
        <Card className="border-red-200 bg-red-50" data-testid="no-requirements-block">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">{t('provider.onboarding.docs.noReqsTitle')}</h3>
            <p className="text-sm text-red-600 max-w-md mx-auto">
              {t('provider.onboarding.docs.noReqsBody')}
            </p>
            <p className="text-xs text-red-400 mt-3">{t('provider.onboarding.docs.applicationLabel', { id: applicationId })}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Group 1: TreatX / Verification docs */}
          {group1.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-blue-600" />
                  {t('provider.onboarding.docs.requiredDocsTitle')}
                </CardTitle>
                <CardDescription>
                  {t('provider.onboarding.docs.requiredDocsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {group1.map(renderDocRow)}
              </CardContent>
            </Card>
          )}

          {/* Group 2: Provider docs */}
          {group2.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  {t('provider.onboarding.docs.providerDocsTitle')}
                </CardTitle>
                <CardDescription>{t('provider.onboarding.docs.providerDocsDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {group2.map(renderDocRow)}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Progress Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-blue-800">{t('provider.onboarding.docs.progressLabel')}</span>
          <span className="text-sm text-blue-600">
            {t('provider.onboarding.docs.progressCount', { uploaded: counts.uploaded, total: counts.total })}
          </span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${counts.total > 0 ? (counts.uploaded / counts.total) * 100 : 0}%` }}
          />
        </div>
        {counts.total > 0 && counts.uploaded < counts.total && (
          <p className="text-xs text-blue-700 mt-2">
            {t('provider.onboarding.docs.progressHint')}
          </p>
        )}
      </div>
    </div>
  );
};

// Step 5: Submission Complete - Pending Validation
const SubmissionCompleteStep = ({ applicationRef, providerName }) => {
  const { t } = useTranslation();
  return (
  <div className="max-w-xl mx-auto text-center">
    <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <Clock className="h-12 w-12 text-amber-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('provider.onboarding.submitted.title')}</h2>
    <p className="text-gray-600 mb-8">
      {t('provider.onboarding.submitted.bodyPart1')} <strong>{t('provider.onboarding.submitted.bodyStrong')}</strong>{t('provider.onboarding.submitted.bodyPart2')}
    </p>

    <Card className="text-left mb-6">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <span className="text-gray-600">{t('provider.onboarding.submitted.refLabel')}</span>
          <span className="font-mono font-bold text-blue-600">{applicationRef}</span>
        </div>
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <span className="text-gray-600">{t('provider.onboarding.submitted.providerLabel')}</span>
          <span className="font-medium">{providerName}</span>
        </div>
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <span className="text-gray-600">{t('provider.onboarding.submitted.statusLabel')}</span>
          <Badge className="bg-amber-100 text-amber-700">
            <Clock className="h-3 w-3 mr-1" /> {t('provider.onboarding.submitted.statusBadge')}
          </Badge>
        </div>

        <h4 className="font-semibold mb-3 mt-4">{t('provider.onboarding.submitted.whatNextTitle')}</h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-medium flex-shrink-0">1</div>
            <div>
              <p className="font-medium">{t('provider.onboarding.submitted.step1Title')}</p>
              <p className="text-sm text-gray-500">{t('provider.onboarding.submitted.step1Desc')}</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-sm font-medium flex-shrink-0">2</div>
            <div>
              <p className="font-medium text-gray-400">{t('provider.onboarding.submitted.step2Title')}</p>
              <p className="text-sm text-gray-400">{t('provider.onboarding.submitted.step2Desc')}</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-sm font-medium flex-shrink-0">
              <Lock className="h-3 w-3" />
            </div>
            <div>
              <p className="font-medium text-gray-400">{t('provider.onboarding.submitted.step3Title')}</p>
              <p className="text-sm text-gray-400">{t('provider.onboarding.submitted.step3Desc')}</p>
            </div>
          </li>
        </ul>

        {/* Locked Section Notice */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
          <div className="flex items-center gap-3 text-gray-500">
            <Lock className="h-5 w-5" />
            <div>
              <p className="font-medium">{t('provider.onboarding.submitted.lockedTitle')}</p>
              <p className="text-xs">{t('provider.onboarding.submitted.lockedBody')}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <p className="text-sm text-gray-500 mb-6">
      {t('provider.onboarding.submitted.emailFooter')}
    </p>

    <Button onClick={() => window.location.href = '/'} variant="outline">
      {t('provider.onboarding.submitted.returnHome')}
    </Button>
  </div>
  );
};

// Main Component
// TREATX-AGREEMENT-MANAGEMENT-MVP-01 — final pre-submit step.
// Renders whatever is CURRENTLY PUBLISHED and required-for-signup for
// the `provider` audience (fetched on mount). Each row displays the
// title / summary / body_html straight from the response so the
// document the applicant sees == the document whose SHA-256 gets
// stamped into user_consents.
const LegalAgreementsStep = ({ agreements, loading, consents, onToggle, openKey, setOpenKey, blocked, unresolvableKeys }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-blue-600" />
          <div>
            <CardTitle>{t('provider.onboarding.agreements.title')}</CardTitle>
            <CardDescription>
              {t('provider.onboarding.agreements.subtitle')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3" data-testid="provider-legal-step">
        {loading && (
          <div className="text-sm text-slate-500" data-testid="provider-legal-loading">
            {t('provider.onboarding.agreements.loading')}
          </div>
        )}
        {!loading && agreements.length === 0 && (
          <div className="text-sm text-red-600" data-testid="provider-legal-empty">
            {t('provider.onboarding.agreements.empty')}
          </div>
        )}
        {!loading && blocked && (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            data-testid="provider-legal-blocked"
          >
            <p className="font-semibold mb-1">{t('provider.onboarding.agreements.blockedTitle')}</p>
            <p>{t('provider.onboarding.agreements.blockedBody')}</p>
            {!!(unresolvableKeys || []).length && (
              <p className="mt-2 text-xs font-mono text-red-600" data-testid="provider-legal-blocked-keys">
                {(unresolvableKeys || []).join(', ')}
              </p>
            )}
          </div>
        )}
        {!loading && !blocked && agreements.map((a) => (
          <DocumentAcceptanceRow
            key={a.content_key}
            agreement={a}
            accepted={!!consents[a.content_key]}
            onToggle={() => onToggle(a.content_key)}
            isOpen={openKey === a.content_key}
            onOpen={() => setOpenKey(openKey === a.content_key ? null : a.content_key)}
          />
        ))}
        <p className="text-xs text-slate-500 mt-3">
          {t('provider.onboarding.agreements.footerNote')}
        </p>
      </CardContent>
    </Card>
  );
};

const DocumentAcceptanceRow = ({ agreement, accepted, onToggle, isOpen, onOpen }) => {
  const { t } = useTranslation();
  const { content_key: consentKey, version, title, summary, body_html } = agreement;
  return (
    <div className={`border rounded-lg ${accepted ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="p-4 flex items-start gap-3">
        <input
          type="checkbox"
          checked={accepted}
          onChange={onToggle}
          className="mt-1 h-4 w-4"
          data-testid={`provider-consent-${consentKey}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                {title || consentKey}
                {version && (
                  <span className="text-[10px] text-slate-400">v{version}</span>
                )}
              </div>
              {summary && (
                <p className="text-xs text-slate-500 mt-0.5">{summary}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onOpen}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0"
              data-testid={`provider-consent-${consentKey}-view`}
            >
              {isOpen ? t('provider.onboarding.agreements.rowHide') : t('provider.onboarding.agreements.rowView')}
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
          {isOpen && (
            <SafeHtml
              html={body_html || t('provider.onboarding.agreements.bodyMissing')}
              className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700 max-h-64 overflow-y-auto"
              testId={`provider-consent-${consentKey}-body`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ProviderOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    currentOnboarding,
    startOnboarding,
    setProviderType,
    setLegalInfo,
    setGeolocationData,
    uploadDocument,
  } = useProviderOnboarding();

  const [step, setStep] = useState(() => {
    const resumed = loadOnboardingProgress('provider');
    return resumed?.step && resumed.step >= 4 && resumed.step <= 5 ? resumed.step : 1;
  });
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [selectedType, setSelectedType] = useState(null);
  const [legalFormData, setLegalFormData] = useState({});
  const [geoFormData, setGeoFormData] = useState({});
  // PROVIDER-ONBOARDING-CLEANUP-01: removed `resolvedGeo` state — coordinates
  // are no longer invented; country/city/address come straight from inputs.
  const [documents, setDocuments] = useState({});
  // BUG-4 — canonical mandatory-document completeness, reported upward by
  // DocumentUploadStep (which owns the requirement set). Drives the step-4
  // gate; `known: false` means the requirement set could not be read.
  const [mandatoryStatus, setMandatoryStatus] = useState({ state: 'loading', known: false, uploaded: 0, total: 0 });
  const [applicationRef, setApplicationRef] = useState(null);
  // BUG-4 — seed the application + step from the post-verification resume
  // anchor so a refresh / remount after email verification continues the
  // wizard instead of restarting it (and instead of letting the provider
  // create a SECOND application for the same business).
  const [applicationId, setApplicationId] = useState(
    () => loadOnboardingProgress('provider')?.application_id || null,
  );

  // ─── TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN ──────────────────
  // The wizard now writes NO permanent rows until the user has clicked
  // the verification link. Between Step 3 (Address) and Step 4
  // (Documents) we insert a `waiting` phase driven by a
  // RegistrationSession envelope. The envelope survives tab close via
  // localStorage — reopening the wizard in the same browser rehydrates
  // it. On successful verification we call /complete which atomically
  // creates the User + Provider + Application, and then the wizard
  // continues into Step 4 with a populated `applicationId`.
  const [phase, setPhase] = useState('form'); // form | waiting_verification
  const [regSession, setRegSession] = useState(() => loadStoredSession('provider') || null);
  const [emailPending, setEmailPending] = useState(null);
  // TREATX-AGREEMENT-MANAGEMENT-MVP-01 — consent UI state.
  // `providerAgreements` is fetched from GET /api/agreements/current
  // on mount. `consents` mirrors the fetched key list.
  const [providerAgreements, setProviderAgreements] = useState([]);
  const [providerAgreementsLoading, setProviderAgreementsLoading] = useState(true);
  // PROVIDER-LEGAL-UNRESOLVABLE-01 — the canonical requirements endpoint now
  // reports REQUIRED documents the legal corpus cannot resolve. When it does,
  // the submit-side evaluator WILL reject the submission, so the step says so
  // instead of offering a form that can never be submitted.
  const [providerAgreementsBlocked, setProviderAgreementsBlocked] = useState(false);
  const [providerUnresolvableKeys, setProviderUnresolvableKeys] = useState([]);
  const [consents, setConsents] = useState({});
  const [openConsentKey, setOpenConsentKey] = useState(null);
  const toggleConsent = (k) => setConsents(prev => ({ ...prev, [k]: !prev[k] }));

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // PRD1.9 SSOT: Read ?type= from URL and map to valid provider type
  useEffect(() => {
    const urlType = searchParams.get('type');
    if (urlType && !selectedType) {
      const typeMap = {
        'clinic': 'clinic',
        'hospital': 'hospital',
        'doctor': 'doctor_practice',
        'doctor_practice': 'doctor_practice',
        'agency': 'agency',
      };
      const mapped = typeMap[urlType.toLowerCase()];
      if (mapped && PROVIDER_TYPES[mapped]) {
        setSelectedType(mapped);
      }
    }
  }, [searchParams, selectedType]);

  // TREATX-CONSENTS-WIRE-PROVIDER-ONBOARDING-01: 6 visible pre-approval steps
  // (was 5). Step 5 = Legal Agreements; Step 6 = Submission Complete.
  // Application submit + consent capture now run on the Step-5 click.
  const steps = [
    { id: 1, title: t('provider.onboarding.step_providerType'), locked: false },
    { id: 2, title: t('provider.onboarding.step_legalInfo'), locked: false },
    { id: 3, title: t('provider.onboarding.step_address'), locked: false },
    { id: 4, title: t('provider.onboarding.step_documents'), locked: false },
    { id: 5, title: t('provider.onboarding.step_agreements'), locked: false },
    { id: 6, title: t('provider.onboarding.step_submit'), locked: false },
    // These are LOCKED until TreatX approval
    { id: 7, title: t('provider.onboarding.step_package'), locked: true },
    { id: 8, title: t('provider.onboarding.step_payment'), locked: true }
  ];

  useEffect(() => {
    if (!currentOnboarding) {
      startOnboarding();
    }
  }, [currentOnboarding, startOnboarding]);

  // TREATX-AGREEMENT-MANAGEMENT-MVP-01 — fetch the current mandatory
  // provider agreements from the canonical registry. This is the sole
  // source of truth for the Legal Agreements step; if it returns 0
  // rows the submit step will surface an explicit "empty" error rather
  // than silently proceed with no consents recorded.
  // TREATX-PROVIDER-LEGAL-CANON-01 — the provider Legal Agreements step
  // now resolves its requirements from the CANONICAL authority
  // (legal_policy_bindings → legal_policy_evaluator) for the
  // `provider_application_submission` journey — the SAME architecture as
  // Patient Registration. The legacy `/api/agreements/current`
  // (required_for_signup) authority is no longer used for provider runtime.
  // If the binding is missing / references an unresolvable required doc,
  // the endpoint returns an empty/partial set and the submit endpoint
  // independently fails closed (no silent required_for_signup fallback).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lang = i18n.language || 'en';
        const params = new URLSearchParams({ locale: lang, role: 'provider_applicant' });
        const res = await fetch(
          `${API_URL}/api/legal/journey/provider_application_submission/requirements?${params.toString()}`
        );
        const data = res.ok ? await res.json() : { requirements: [] };
        const mapped = (Array.isArray(data.requirements) ? data.requirements : []).map((r) => ({
          content_key: r.content_key,
          version: r.version,
          title: r.title,
          summary: r.summary || '',
          body_html: r.body_html || '',
          locale: r.locale,
          is_locale_fallback: r.is_locale_fallback,
          acceptance_mechanism: r.acceptance_mechanism || 'checkbox',
          required: r.required,
          blocking: r.blocking,
        }));
        if (!cancelled) {
          setProviderAgreements(mapped);
          setProviderUnresolvableKeys(
            Array.isArray(data.unresolvable_required_keys) ? data.unresolvable_required_keys : []
          );
          setProviderAgreementsBlocked(!!data.blocked);
          setProviderAgreementsLoading(false);
        }
      } catch (_e) {
        if (!cancelled) {
          setProviderAgreements([]);
          setProviderUnresolvableKeys([]);
          setProviderAgreementsBlocked(false);
          setProviderAgreementsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  // TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN — resume hydration.
  // If the browser was closed mid-signup, on remount we find the
  // saved RegistrationSession envelope in localStorage. Rehydrate the
  // form fields from `form_data` (echoed by the server on poll) and
  // pivot to the `waiting_verification` phase so the user picks up
  // exactly where they left off.
  useEffect(() => {
    if (regSession && !applicationId) {
      // Hydrate the pre-verify form fields from the persisted session.
      const persisted = loadStoredSession('provider');
      if (persisted?.session_id === regSession.session_id) {
        setPhase('waiting_verification');
      }
    }
     
  }, []);

  // TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN — canonical entry point.
  // Sends the collected form data to the RegistrationSession state
  // machine. NO User / Provider / Application is written yet — the
  // backend creates a `pending_verification` session and dispatches
  // the verification email. On success we advance to the `waiting`
  // phase and start polling; the actual application is created ONLY
  // after the user clicks the email link.
  const startRegistrationFlow = async () => {
    // PROVIDER-IDENTITY-MANDATORY-BRAND (Batch 2): organizations must EXPLICITLY
    // declare BOTH Legal Entity Name and Public Brand (no silent legal→brand
    // copy). Individual practitioners (doctor_practice) declare a single
    // personal identity (full name) used for both legal and public display.
    const _isIndividual = (selectedType || 'clinic') === 'doctor_practice';
    const _legal = (legalFormData.legalName || legalFormData.fullName || '').trim();
    const _brand = _isIndividual
      ? (legalFormData.fullName || legalFormData.legalName || '').trim()
      : (legalFormData.brandName || '').trim();
    if (!_legal) {
      toast.error(t('provider.onboarding.legalEntityNameRequired'));
      return;
    }
    if (!_isIndividual && !_brand) {
      toast.error(t('provider.onboarding.brandNameRequired'));
      return;
    }
    try {
      const out = await startRegistrationSession({
        role: 'provider',
        email: legalFormData.email || '',
        password: legalFormData.password || '',
        formData: {
          provider_type: selectedType || 'clinic',
          legal_entity_name: _legal,
          brand_name: _brand,
          country: geoFormData.country || '',
          city: geoFormData.city || '',
          address: geoFormData.address || '',
          phone: legalFormData.phone || '',
        },
      });
      const envelope = {
        session_id: out.session_id,
        session_secret: out.session_secret,
        email: out.email,
        role: out.role,
        expires_at: out.expires_at,
        email_mode: out.email_mode,
        activation_link: out.activation_link || null,
      };
      persistSession('provider', envelope);
      setRegSession(envelope);
      setEmailPending({ email: envelope.email, email_mode: envelope.email_mode });
      setPhase('waiting_verification');
      toast({
        title: t('provider.onboarding.toastVerifyEmailTitle'),
        description: t('provider.onboarding.toastVerifyEmailDesc'),
      });
      return true;
    } catch (err) {
      const detail = err?.detail || err?.message || '';
      if (err.status === 409 && String(detail).includes('email_reserved_by_pending_session')) {
        toast({
          title: t('provider.onboarding.toastEmailReservedTitle'),
          description: t('provider.onboarding.toastEmailReservedDesc'),
          variant: 'destructive',
        });
        return false;
      }
      console.error('[Onboarding][RegistrationSession] start failed:', err);
      toast({ title: t('provider.onboarding.toastSignupFailedTitle'), description: detail || t('provider.onboarding.toastSignupFailedDesc'), variant: 'destructive' });
      return false;
    }
  };

  // Called by the WaitingForEmailVerificationStep as soon as the
  // backend reports the session flipped to `email_verified`. Runs the
  // atomic /complete call, receives the fresh application_id, and
  // advances the wizard to Step 4 (Documents).
  const handleEmailVerified = async () => {
    if (!regSession) return;
    setLoading(true);
    try {
      const done = await completeSession({
        sessionId: regSession.session_id,
        sessionSecret: regSession.session_secret,
      });
      const appId = done.application_id;
      if (!appId) {
        toast({ title: t('provider.onboarding.toastUnexpectedTitle'), description: t('provider.onboarding.toastUnexpectedDesc'), variant: 'destructive' });
        setLoading(false);
        return;
      }
      setApplicationId(appId);
      setApplicationRef(appId);
      setPhase('form');
      setStep(4);
      // BUG-4 — record the canonical application + reached step so a refresh
      // or remount resumes here. Written only AFTER the server confirmed the
      // application, so the anchor can never point at a non-existent one.
      persistOnboardingProgress('provider', { application_id: appId, step: 4 });
      clearStoredSession('provider');
      setRegSession(null);
      setEmailPending(null);
      toast({
        title: t('provider.onboarding.toastEmailVerifiedTitle'),
        description: t('provider.onboarding.toastEmailVerifiedDesc'),
      });
    } catch (err) {
      console.error('[Onboarding][RegistrationSession] complete failed:', err);
      toast({ title: t('provider.onboarding.toastCompleteFailedTitle'), description: err?.detail || err?.message || t('provider.onboarding.toastCompleteFailedDesc'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const cancelWaiting = () => {
    setPhase('form');
    setStep(3);
    clearStoredSession('provider');
    clearOnboardingProgress('provider');
    setRegSession(null);
    setEmailPending(null);
  };

  const resendVerification = async () => {
    // Idempotent restart — same email+password re-uses the session and
    // rotates the verification token so a new email is dispatched.
    const ok = await startRegistrationFlow();
    if (ok) {
      toast({ title: t('provider.onboarding.toastResendTitle'), description: t('provider.onboarding.toastResendDesc') });
    }
  };

  const handleNext = async () => {
    setLoading(true);

    switch (step) {
      case 1:
        if (!selectedType) {
          toast({ title: t('provider.onboarding.toastSelectionTitle'), description: t('provider.onboarding.toastSelectionDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        setProviderType(selectedType);
        break;
      case 2:
        if (!legalFormData.email) {
          toast({ title: t('provider.onboarding.toastMissingInfoTitle'), description: t('provider.onboarding.toastMissingInfoDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        if (!legalFormData.password || legalFormData.password.length < 8) {
          toast({ title: t('provider.onboarding.toastPasswordReqTitle'), description: t('provider.onboarding.toastPasswordReqDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        if (legalFormData.password !== legalFormData.confirmPassword) {
          toast({ title: t('provider.onboarding.toastPasswordMismatchTitle'), description: t('provider.onboarding.toastPasswordMismatchDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        setLegalInfo(legalFormData);
        break;
      case 3: {
        if (!geoFormData.country || !geoFormData.city || !geoFormData.address) {
          toast({ title: t('provider.onboarding.toastAddressReqTitle'), description: t('provider.onboarding.toastAddressReqDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        // Persist address to onboarding context (no fake coordinates)
        setGeolocationData({ address: geoFormData.address, country: geoFormData.country, city: geoFormData.city });
        // TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN: Step 3 no longer
        // writes any DB rows. It kicks off a RegistrationSession — a
        // temporary, expiry-bound envelope — and pivots the wizard
        // into the `waiting_verification` phase. The atomic User +
        // Provider + Application creation happens later, on the
        // /complete call fired from the waiting step.
        if (!applicationId) {
          const started = await startRegistrationFlow();
          if (!started) {
            setLoading(false);
            return;
          }
          // Do NOT advance step here — the phase switch is the
          // effective progression, and the waiting step will call
          // setStep(4) after verification.
          setLoading(false);
          return;
        }
        break;
      }
      case 4: {
        // Documents are already uploaded via API in DocumentUploadStep.
        // DOC-VALIDATION-01 — FAIL CLOSED. The gate previously fell through
        // to a permissive "at least one document uploaded" check whenever the
        // canonical requirement set was unknown (still loading, read failed,
        // malformed response, or none configured), so Next could advance
        // without the mandatory set ever being validated. Progression now
        // requires a SUCCESSFULLY VALIDATED canonical requirement set whose
        // mandatory documents are all uploaded. No document types or counts
        // are hardcoded and no second requirements authority is introduced.
        if (mandatoryStatus.state === 'loading') {
          toast({
            title: t('provider.onboarding.toastPleaseWaitTitle'),
            description: t('provider.onboarding.toastDocsReqLoadingDesc'),
          });
          setLoading(false);
          return;
        }
        if (mandatoryStatus.state === 'empty') {
          toast({
            title: t('provider.onboarding.toastDocsReqNoneTitle'),
            description: t('provider.onboarding.toastDocsReqNoneDesc'),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        if (mandatoryStatus.state !== 'ready') {
          toast({
            title: t('provider.onboarding.toastDocsReqUnavailableTitle'),
            description: t('provider.onboarding.toastDocsReqUnavailableDesc'),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        if (mandatoryStatus.uploaded < mandatoryStatus.total) {
          toast({
            title: t('provider.onboarding.toastDocsRequiredTitle'),
            description: t('provider.onboarding.toastDocsMandatoryMissingDesc', {
              uploaded: mandatoryStatus.uploaded,
              total: mandatoryStatus.total,
            }),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        // TREATX-CONSENTS-WIRE-PROVIDER-ONBOARDING-01: do NOT submit yet.
        // Advance to the new Legal Agreements step where the submit happens
        // alongside the consent capture in a single atomic POST.
        break;
      }
      case 5: {
        // Block submit unless every mandatory consent is accepted
        // AND the dynamic list actually loaded.
        if (providerAgreementsLoading) {
          toast({ title: t('provider.onboarding.toastPleaseWaitTitle'), description: t('provider.onboarding.toastPleaseWaitDesc') });
          setLoading(false);
          return;
        }
        if (providerAgreementsBlocked) {
          toast({
            title: t('provider.onboarding.agreements.blockedTitle'),
            description: t('provider.onboarding.toastSubmitLegalUnresolvableDesc', {
              keys: (providerUnresolvableKeys || []).join(', '),
            }),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        if (providerAgreements.length === 0) {
          toast({
            title: t('provider.onboarding.toastAgreementsUnavailableTitle'),
            description: t('provider.onboarding.toastAgreementsUnavailableDesc'),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        const missing = providerAgreements.filter(a => !consents[a.content_key]);
        if (missing.length > 0) {
          toast({
            title: t('provider.onboarding.toastAcceptanceReqTitle'),
            description: t('provider.onboarding.toastAcceptanceReqDesc', { count: providerAgreements.length }),
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        if (!applicationId) {
          toast({ title: t('provider.onboarding.toastSubmissionFailedTitle'), description: t('provider.onboarding.toastSubmissionAppMissingDesc'), variant: 'destructive' });
          setLoading(false);
          return;
        }
        // Resolve current content versions for each accepted key.
        const language = i18n.language || 'en';
        const consentsPayload = providerAgreements.map((a) => ({
          content_key: a.content_key,
          content_version: a.version,
          language: a.locale || language,
          country_context: geoFormData.country || null,
          acceptance_mechanism: a.acceptance_mechanism || 'checkbox',
        }));
        try {
          // Application-private mutation: canonical credentialed api client
          // (status_session cookie + X-CSRF-Token). Backend gate: submit
          // requires the application-bound session established at /complete.
          const submitRes = await api.post(`/provider/applications/${applicationId}/submit`, {
            consents: consentsPayload,
          });
          const submitData = submitRes.data;
          if (!submitData.success) {
            toast({ title: t('provider.onboarding.toastSubmissionFailedTitle'), description: submitData.message || t('provider.onboarding.toastSubmissionCouldNotDesc'), variant: 'destructive' });
            setLoading(false);
            return;
          }
          if (submitData.consents_recorded !== consentsPayload.length) {
            // Some consents weren't accepted by the engine (version mismatch).
            // Block — provider must retry rather than proceed with a partial trail.
            toast({
              title: t('provider.onboarding.toastConsentCaptureTitle'),
              description: t('provider.onboarding.toastConsentCaptureDesc', { recorded: submitData.consents_recorded, total: consentsPayload.length }),
              variant: 'destructive',
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('[Onboarding] Submit error:', e);
          const d = e?.response?.data;
          // PROVIDER-LEGAL-UNRESOLVABLE-01 — the canonical submit enforcer
          // answers with {code, problems:[{content_key, reason}]} and an
          // EMPTY `message`, which used to collapse into the misleading
          // "Network error". Map the canonical code to a clear, localized
          // reason. Only backend-issued identifiers and codes are rendered
          // (as plain text) — never raw backend HTML.
          const problems = Array.isArray(d?.problems) ? d.problems : [];
          const keys = problems.map(p => p?.content_key).filter(Boolean).join(', ');
          const reasons = new Set(problems.map(p => p?.reason));
          let msg;
          if (d?.code === 'LEGAL_REQUIREMENTS_NOT_MET') {
            msg = reasons.has('unresolvable_required_document')
              ? t('provider.onboarding.toastSubmitLegalUnresolvableDesc', { keys })
              : t('provider.onboarding.toastSubmitLegalNotMetDesc', { keys });
          } else if (typeof d?.detail === 'string' && d.detail.trim()) {
            msg = d.detail;
          } else if (typeof d?.message === 'string' && d.message.trim()) {
            msg = d.message;
          } else if (typeof d?.code === 'string' && d.code.trim()) {
            msg = t('provider.onboarding.toastSubmissionCodeDesc', { code: d.code });
          } else {
            msg = t('provider.onboarding.toastSubmissionNetworkDesc');
          }
          toast({ title: t('provider.onboarding.toastSubmissionFailedTitle'), description: msg, variant: 'destructive' });
          setLoading(false);
          return;
        }
        break;
      }
      default:
        break;
    }

    setTimeout(() => {
      setStep(prev => {
        const next = Math.min(prev + 1, 6); // Max step 6 before approval
        // BUG-4 — keep the resume anchor in step with the wizard. Step 6 is
        // terminal (application submitted), so the anchor is retired there.
        if (applicationId && next >= 4 && next <= 5) {
          persistOnboardingProgress('provider', { application_id: applicationId, step: next });
        } else if (next >= 6) {
          clearOnboardingProgress('provider');
        }
        return next;
      });
      setLoading(false);
    }, 300);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(prev => prev - 1);
    } else {
      navigate('/');
    }
  };

  const handleLegalChange = (field, value) => {
    setLegalFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGeoChange = (field, value) => {
    setGeoFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDocUpload = (docId, fileData) => {
    setDocuments(prev => ({ ...prev, [docId]: fileData }));
  };

  const getProviderName = () => {
    return legalFormData.legalName || legalFormData.fullName || t('provider.onboarding.fallbackOrgName');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">{t('provider.onboarding.pageTitle')}</h1>
              <p className="text-xs text-gray-500">{t('provider.onboarding.pageSubtitle')}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => navigate('/login')}>
            {t('provider.onboarding.alreadyRegistered')}
          </Button>
        </div>
      </header>

      {/* Progress */}
      <StepIndicator currentStep={step} steps={steps} />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-4 pb-28">
        {/* TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN — waiting phase
            intercepts the Step 3 → Step 4 transition. During this
            phase no permanent DB rows exist yet; we're polling the
            RegistrationSession. Once the backend flips to
            `email_verified`, handleEmailVerified() calls /complete to
            atomically create the User + Provider + Application and
            sets step=4 (Documents) to continue the wizard seamlessly. */}
        {phase === 'waiting_verification' && regSession && (
          <WaitingForEmailVerificationStep
            sessionId={regSession.session_id}
            sessionSecret={regSession.session_secret}
            email={regSession.email}
            emailMode={regSession.email_mode}
            activationLinkDev={regSession.activation_link}
            onVerified={handleEmailVerified}
            onResend={resendVerification}
            onCancel={cancelWaiting}
          />
        )}

        {phase === 'form' && step === 1 && <ProviderTypeStep selected={selectedType} onSelect={setSelectedType} />}
        {phase === 'form' && step === 2 && <LegalInfoStep providerType={selectedType} formData={legalFormData} onChange={handleLegalChange} />}
        {phase === 'form' && step === 3 && <AddressStep formData={geoFormData} onChange={handleGeoChange} />}
        {phase === 'form' && step === 4 && (
          <DocumentUploadStep
            applicationId={applicationId}
            documents={documents}
            onUpload={handleDocUpload}
            onUploadComplete={setMandatoryStatus}
          />
        )}
        {phase === 'form' && step === 5 && (
          <LegalAgreementsStep
            agreements={providerAgreements}
            loading={providerAgreementsLoading}
            blocked={providerAgreementsBlocked}
            unresolvableKeys={providerUnresolvableKeys}
            consents={consents}
            onToggle={toggleConsent}
            openKey={openConsentKey}
            setOpenKey={setOpenConsentKey}
          />
        )}
        {phase === 'form' && step === 6 && <SubmissionCompleteStep applicationRef={applicationRef} providerName={getProviderName()} />}
      </div>

      {/* PROVIDER-ONBOARDING-IPAD-VIEWPORT-FIX-01 (2026-02):
          Primary action bar pinned to viewport bottom so the Back/Continue
          row remains visible on tablet portrait viewports (iPad Mini 768×1024)
          and mobile, where Step 2 / 4 / 5 forms naturally exceed viewport
          height. Form scrolls behind; sticky-bottom guarantees the next step
          is always one tap away. Content wrapper above carries pb-28 to
          prevent the sticky bar from covering the last field.

          Waiting phase hides the sticky action bar — the waiting step
          renders its own Cancel / Re-send / Waiting controls. */}
      {phase === 'form' && step < 6 && (
        <div
          className="sticky bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_12px_rgba(15,23,42,0.05)]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="onboarding-sticky-action-bar"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between">
            <Button variant="outline" onClick={handleBack} disabled={loading}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {step === 1 ? t('provider.onboarding.actionCancel') : t('provider.onboarding.actionBack')}
            </Button>
            <Button
              onClick={handleNext}
              disabled={
                loading
                || (step === 5 && (providerAgreementsLoading || providerAgreementsBlocked || providerAgreements.length === 0 || providerAgreements.some(a => !consents[a.content_key])))
              }
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="onboarding-continue-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('provider.onboarding.actionProcessing')}
                </>
              ) : (
                <>
                  {step === 5 ? t('provider.onboarding.actionSubmitValidation') : t('provider.onboarding.actionContinue')}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderOnboarding;